import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  usePharmacySalesTrash,
  useRestorePharmacySale,
  usePermanentlyDeletePharmacySale,
  useBulkRestorePharmacySales,
  useBulkPermanentlyDeletePharmacySales,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { joinFullName } from '../../utils/patientName';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { PharmacyLayout } from './PharmacyLayout';
import {
  Loader2,
  AlertCircle,
  Search,
  X,
  User,
  RotateCcw,
  Trash2,
  ArrowLeft,
} from 'lucide-react';

const LIMIT = 20;

const money = (value: number) => `৳${(Number(value) || 0).toLocaleString()}`;

export function PharmacySalesTrash() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmTarget, setConfirmTarget] = useState<{ mode: 'single' | 'bulk'; sale?: any } | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = usePharmacySalesTrash(page, LIMIT, debouncedSearch.trim() || undefined);
  const restoreSale = useRestorePharmacySale();
  const permanentDelete = usePermanentlyDeletePharmacySale();
  const bulkRestore = useBulkRestorePharmacySales();
  const bulkDelete = useBulkPermanentlyDeletePharmacySales();

  const sales = data?.data || [];
  const meta = data?.meta;

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [debouncedSearch]);

  const allSelected = sales.length > 0 && sales.every((sale: any) => selectedIds.includes(sale.id));
  const someSelected = selectedIds.length > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !sales.some((sale: any) => sale.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...sales.map((sale: any) => sale.id)])));
    }
  };

  const toggleSelectOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const customerLabel = (sale: any) => {
    if (sale.patient) {
      const name = joinFullName(sale.patient);
      return name || sale.patient.registrationNumber || 'Patient';
    }
    return sale.customerName || 'Walk-in';
  };

  const handleRestore = async (id: string) => {
    await restoreSale.mutateAsync(id);
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  const handleBulkRestore = async () => {
    try {
      await bulkRestore.mutateAsync(selectedIds);
      toast.success(`Restored ${selectedIds.length} sale(s)`);
      setSelectedIds([]);
    } catch {
      toast.error('Failed to restore some sales');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    try {
      if (confirmTarget.mode === 'single') {
        await permanentDelete.mutateAsync(confirmTarget.sale.id);
        setSelectedIds((prev) => prev.filter((x) => x !== confirmTarget.sale.id));
      } else {
        await bulkDelete.mutateAsync(selectedIds);
        toast.success(`Permanently deleted ${selectedIds.length} sale(s)`);
        setSelectedIds([]);
      }
    } catch {
      if (confirmTarget.mode === 'bulk') toast.error('Failed to delete some sales');
    } finally {
      setConfirmTarget(null);
    }
  };

  const busy = bulkRestore.isPending || bulkDelete.isPending;

  return (
    <PharmacyLayout
      header={
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/pharmacy')}
            className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-danger/10 rounded-lg">
              <Trash2 className="w-6 h-6 text-danger" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text">Sales Trash</h1>
              <p className="text-sm text-text-muted">Restore sales or delete them permanently</p>
            </div>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-surface rounded-xl border border-border p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search trashed sales..."
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </div>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>

        {someSelected && (
          <div className="flex flex-wrap items-center gap-2 bg-primary/5 border border-primary/20 rounded-xl p-3">
            <span className="text-sm text-text mr-2">
              <span className="font-medium">{selectedIds.length}</span> selected
            </span>
            <button
              onClick={handleBulkRestore}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-success text-white text-xs font-medium hover:bg-success/90 disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkRestore.isPending ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
              Restore
            </button>
            <button
              onClick={() => setConfirmTarget({ mode: 'bulk' })}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-danger text-white text-xs font-medium hover:bg-danger/90 disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDelete.isPending ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              Delete permanently
            </button>
            <button
              onClick={() => setSelectedIds([])}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg text-text-muted hover:text-text border border-border text-xs font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={12} />
              Clear selection
            </button>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
            <AlertCircle size={18} />
            <span>Failed to load trashed sales</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="bg-surface rounded-xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-background/50">
                    <th className="px-4 py-3 text-left w-10">
                      <input
                        ref={headerCheckboxRef}
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        disabled={sales.length === 0}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                        title="Select all"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Sale</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Customer</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Trashed</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sales.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                        {search ? 'No trashed sales match your search' : 'Trash is empty'}
                      </td>
                    </tr>
                  ) : (
                    sales.map((sale: any) => (
                      <tr key={sale.id} className="hover:bg-background/50 transition-colors">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(sale.id)}
                            onChange={() => toggleSelectOne(sale.id)}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-text">{sale.saleNumber}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <User size={13} className="text-primary" />
                            </div>
                            <span className="text-sm text-text">{customerLabel(sale)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-text text-right font-medium">
                          {money(sale.totalAmount)}
                        </td>
                        <td className="px-4 py-3 text-sm text-text-muted">
                          {sale.deletedAt ? new Date(sale.deletedAt).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleRestore(sale.id)}
                              disabled={restoreSale.isPending}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors disabled:opacity-50"
                              title="Restore"
                            >
                              <RotateCcw size={16} />
                            </button>
                            <button
                              onClick={() => setConfirmTarget({ mode: 'single', sale })}
                              disabled={permanentDelete.isPending}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                              title="Delete permanently"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {meta && (
              <Pagination
                page={meta.page}
                totalPages={meta.totalPages}
                total={meta.total}
                limit={meta.limit}
                onPageChange={setPage}
                itemName="trashed sales"
              />
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmTarget !== null}
        title={confirmTarget?.mode === 'bulk' ? 'Delete sales permanently' : 'Delete sale permanently'}
        description={
          confirmTarget?.mode === 'bulk' ? (
            `Permanently delete ${selectedIds.length} selected sale(s)? Stock will be returned and this cannot be undone.`
          ) : (
            <>
              Permanently delete <strong>{confirmTarget?.sale?.saleNumber}</strong>? Stock will be
              returned and this cannot be undone.
            </>
          )
        }
        confirmLabel="Delete permanently"
        loading={busy || permanentDelete.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmTarget(null)}
      />
    </PharmacyLayout>
  );
}
